import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useRef, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { GeocodeResult, getGeocode } from 'use-places-autocomplete';
import { AddressFormContext } from './address-form.context';
import { addressFormSchema } from './address-form.schema';
import { useSnackbar } from 'notistack';
import { GeocodeAddress, LatLng, Place } from './address-form.types';
import { geocodeToAddress } from './address-form.utils';

export const useMapsAddress = (name, addressKey) => {
  const {
    placeContext: { address },
  } = useAddressFormContext();
  const { setValue } = useFormContext();

  useEffect(() => {
    if (!!address)
      setValue(name, address[addressKey], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
  }, [address, addressKey, name, setValue]);
};

export const useMapsGeo = (name, geoKey) => {
  const {
    placeContext: { latLng },
  } = useAddressFormContext();
  const { setValue } = useFormContext();

  useEffect(() => {
    if (!!latLng)
      setValue(name, latLng[geoKey], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
  }, [geoKey, latLng, name, setValue]);
};

export const defaultAddressValueMap = {
  streetAddress1: 'long_name',
  streetAddress11: 'long_name',
  city: 'long_name',
  region: 'short_name',
  country: 'short_name',
  postalCode: 'long_name',
};

export type UsePlaceOptions = {
  addressValueMap: {
    streetAddress1: string;
    streetAddress11: string;
    city: string;
    region: string;
    country: string;
    postalCode: string;
  };
};

export type UsePlaceReturnValue = {
  place: Place;
  setPlace: React.Dispatch<React.SetStateAction<Place>>;
  geocode: any;
  address: GeocodeAddress | {};
  latLng: {
    latitude: number;
    longitude: number;
  };
};

export const usePlace = (
  options: UsePlaceOptions = { addressValueMap: defaultAddressValueMap },
): UsePlaceReturnValue => {
  const [place, setPlace] = useState<Place>();
  const [geocode, setGeocode] = useState<GeocodeResult>();
  const [address, setAddress] = useState<GeocodeAddress | {}>();
  const [latLng, setLatLng] = useState<LatLng>();
  const optionsRef = useRef(options);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const execute = async () => {
      try {
        const result = await getGeocode({ placeId: place.place_id });
        setGeocode(result[0]);
        setAddress(
          geocodeToAddress(result[0], optionsRef.current.addressValueMap),
        );
        setLatLng({
          latitude: Math.round(result[0].geometry.location.lat() * 1e6) / 1e6,
          longitude: Math.round(result[0].geometry.location.lng() * 1e6) / 1e6,
        });
      } catch (e) {
        enqueueSnackbar(e, { severity: 'error' });
      }
    };

    if (place) {
      execute();
    } else {
      setGeocode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  return { place, setPlace, geocode, address, latLng };
};

export const useAddressFormContext = () => useContext(AddressFormContext);

export const useAddressForm = (schema = addressFormSchema) => {
  return useForm({
    resolver: yupResolver(schema),
    defaultValues: schema.cast({}),
  });
};

export const useAddressFieldNames = () =>
  useContext(AddressFormContext).fieldNames;
